export const READING_POSITION_VERSION = 1 as const;
export const READING_POSITION_MIN_PROGRESS = 0.05;
export const READING_POSITION_STORAGE_PREFIX = "pagebunny:reading-position:v1:";

export interface StoredReadingPosition {
  version: typeof READING_POSITION_VERSION;
  progress: number;
  contentKey: string;
}

const buildStorageKey = (userId: string, sectionId: string): string =>
  `${READING_POSITION_STORAGE_PREFIX}${encodeURIComponent(
    userId,
  )}:${encodeURIComponent(sectionId)}`;

const buildTextFingerprint = (text: string): string => {
  let hash = 0x811c9dc5;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16).padStart(8, "0");
};

const removeStorageKey = (storageKey: string): void => {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Device storage can be unavailable because of browser privacy settings.
  }
};

const isStoredReadingPosition = (
  value: unknown,
): value is StoredReadingPosition => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    record.version === READING_POSITION_VERSION &&
    typeof record.contentKey === "string" &&
    typeof record.progress === "number" &&
    Number.isFinite(record.progress) &&
    record.progress >= READING_POSITION_MIN_PROGRESS &&
    record.progress <= 1
  );
};

export function buildReadingContentKey(
  adjustedReadingSectionId: string | undefined,
  adjustedText: string,
): string {
  if (adjustedReadingSectionId) {
    return `adjusted:${adjustedReadingSectionId}`;
  }

  return `text:${adjustedText.length}:${buildTextFingerprint(adjustedText)}`;
}

export function getReadingPosition(
  userId: string,
  sectionId: string,
  contentKey: string,
): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = buildStorageKey(userId, sectionId);
  let storedValue: string | null;

  try {
    storedValue = window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }

  if (storedValue === null) {
    return null;
  }

  let storedPosition: unknown;

  try {
    storedPosition = JSON.parse(storedValue);
  } catch {
    removeStorageKey(storageKey);
    return null;
  }

  if (
    !isStoredReadingPosition(storedPosition) ||
    storedPosition.contentKey !== contentKey
  ) {
    removeStorageKey(storageKey);
    return null;
  }

  return storedPosition.progress;
}

export function saveReadingPosition(
  userId: string,
  sectionId: string,
  contentKey: string,
  progress: number,
): void {
  if (typeof window === "undefined") {
    return;
  }

  if (!Number.isFinite(progress)) {
    return;
  }

  const normalizedProgress = Math.min(1, Math.max(0, progress));
  const storageKey = buildStorageKey(userId, sectionId);

  if (normalizedProgress < READING_POSITION_MIN_PROGRESS) {
    removeStorageKey(storageKey);
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: READING_POSITION_VERSION,
        progress: normalizedProgress,
        contentKey,
      }),
    );
  } catch {
    // Device storage can be unavailable because of quota or privacy settings.
  }
}

export function clearReadingPosition(userId: string, sectionId: string): void {
  if (typeof window === "undefined") {
    return;
  }

  removeStorageKey(buildStorageKey(userId, sectionId));
}

export function clearReadingPositionsForUser(userId: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const userStoragePrefix = `${READING_POSITION_STORAGE_PREFIX}${encodeURIComponent(
    userId,
  )}:`;
  let storageLength: number;

  try {
    storageLength = window.localStorage.length;
  } catch {
    return;
  }

  const storageKeys: string[] = [];

  for (let index = 0; index < storageLength; index += 1) {
    let storageKey: string | null;

    try {
      storageKey = window.localStorage.key(index);
    } catch {
      return;
    }

    if (storageKey?.startsWith(userStoragePrefix)) {
      storageKeys.push(storageKey);
    }
  }

  storageKeys.forEach(removeStorageKey);
}
